/**
 * Палитрата на календара: кой цвят какво значи, каква форма носи и как се пази от сблъсък.
 *
 * Поводът е измерен на production 2026-09-05: брандовият secondary на училището беше
 * `#dc2626`, а типът „празник" в календара — същият `#dc2626`. Помощническият час и
 * „Ден на Българската азбука" излизаха като две еднакви точки с различно значение.
 * Сблъсъкът беше СЛУЧАЕН — зависеше от бранд цвета — и щеше да се върне при следващото
 * училище с червено в бранда.
 *
 * Затова семантичните цветове не идват от бранда. От него остава само `primary`, за
 * „моите часове"; останалите са фиксирани тук и при сблъсък отстъпва семантичният, не
 * брандът, който е чужда собственост.
 *
 * 🔴 ДВА КАНАЛА, НЕ ЕДИН. Шест цвята не се събират в пространството, което остава след
 * deuteranopia — там осите са практически две (светлота и синьо-жълто). Измерено: при
 * праг 35 четири от осем представителни бранда падат. Затова ролите са разделени на две
 * семейства, които се различават по ФОРМА: моите часове са кръгове, неучебните дни са
 * квадрати. Тогава цветовете се състезават само вътре в семейството си — три срещу три —
 * и решението съществува. Формата не е украса, а вторият носител на значението: тя
 * работи и при монохромен печат, и при най-тежката цветна слепота.
 *
 * Прагът е измерен срещу човешка преценка, не избран на теория. Owner-ът гледа реалната
 * решетка на екран и отхвърли две двойки, които мереха 30.0 и 31.0 — тоест праг 25 ги
 * пускаше. Оттам SAME = 35.
 */

/**
 * Всички роли, в реда, в който се показват на човек.
 *
 * 🔴 ЕДИНСТВЕНИЯТ списък, и типът се извежда ОТ НЕГО, не обратното. Легендата и
 * тестовете четат него — второ копие вече веднъж пропусна роля: „вноска по такса"
 * получи цвят и форма, но остана извън легендата, а тестът заковаваше същия непълен
 * списък и минаваше.
 *
 * Предишната версия стоеше като `readonly CalendarRole[]` под отделно обявен съюз и
 * коментар „роля, добавена тук, се появява навсякъде или чупи компилацията". Измерено:
 * НЕ чупеше — масив от два елемента с тип `readonly CalendarRole[]` минава `tsc --strict`
 * без дума. Тоест гардът обещаваше проверка, която не правеше. Сега съюзът е производен:
 * непълен списък е невъзможен по конструкция.
 */
export const CALENDAR_ROLES = [
  'lesson',
  'assistant',
  'lastLesson',
  'vacation',
  'holiday',
  'noSchool',
  'fee',
] as const;

/** Ролите, които календарът рисува. */
export type CalendarRole = (typeof CALENDAR_ROLES)[number];

/** Цветовете на календара, след разрешаване на сблъсъци. */
export type CalendarPalette = Record<CalendarRole, string>;

/** Формата на точката — вторият канал на значението. */
export type MarkShape = 'circle' | 'square';

/**
 * Семейството решава И формата, И колко строг е прагът между две роли.
 * A = час на учителя (кръг) · B = всичко останало на календара (квадрат).
 *
 * „Вноска по такса" е в B, макар да не е неучебен ден: тя не е час, а точно това
 * различава формата. Отделно трето семейство би искало трета форма, а на 4px
 * различими форми са две — кръг и квадрат; триъгълник или ромб на четири пиксела
 * са същото петно.
 */
const FAMILY: Record<CalendarRole, 'A' | 'B'> = {
  lesson: 'A',
  assistant: 'A',
  lastLesson: 'A',
  vacation: 'B',
  holiday: 'B',
  noSchool: 'B',
  fee: 'B',
};

/** Кръг за часовете, квадрат за неучебните дни. На 4px това се различава; куха точка — не. */
export function markShape(role: CalendarRole): MarkShape {
  return FAMILY[role] === 'A' ? 'circle' : 'square';
}

/**
 * Праг ВЪТРЕ в семейството: там цветът е единственият различител, защото формата е обща.
 * 35 идва от преценка на човек върху реалната решетка (виж шапката).
 */
export const MIN_DELTA_E_SAME = 35;

/**
 * Праг МЕЖДУ семействата: формата вече различава, затова изискването е по-меко — но не
 * отпада. Без него алгоритъмът стигна до „помощник" тюркоазено и „неучебен ден" топло
 * сиво на ΔE 11.4: различими по форма, но на практика един цвят.
 */
export const MIN_DELTA_E_CROSS = 25;

/**
 * Кандидати по роля: първият е предпочитаният, останалите са резерви.
 * Резервът се избира спрямо ВЕЧЕ ЗАЕТИТЕ цветове, не само спрямо бранда — иначе замяната
 * поправя една двойка и чупи друга (измерено: при син бранд „помощник"/„последен час"
 * падаха на ΔE 8.2, докато изборът гледаше само primary).
 */
const CANDIDATES: Record<Exclude<CalendarRole, 'lesson'>, readonly string[]> = {
  // „Последен час" = краят на годината. Решение на owner: цвят на свободата — небе и море.
  lastLesson: ['#22d3ee', '#06b6d4', '#67e8f9', '#0e7490', '#a21caf', '#4c1d95', '#fde68a'],
  // Помощник: индиго за повечето брандове; маслинено и охра са изходите при син/виолетов бранд.
  assistant: ['#4338ca', '#1d4ed8', '#0284c7', '#3f6212', '#a16207', '#0f766e'],
  // Ваканция: amber-400, не amber-500. Разликата е решаваща при ЧЕРВЕН бранд —
  // 30.0 срещу 38.8 при deuteranopia, тоест по-светлото прави червения бранд използваем.
  vacation: ['#fbbf24', '#fcd34d', '#fde68a', '#f59e0b', '#ca8a04', '#fef3c7'],
  // Празник: магента, не червено. Червеното е невъзможно срещу зелен бранд (при
  // deuteranopia двете се разминават само по светлота), а тъмното бордо, което минаваше,
  // изглеждаше почти черно на 4px — отхвърлено при преглед на екран.
  holiday: ['#86198f', '#701a75', '#9f1239', '#4c0519', '#365314'],
  // Неучебен ден: топло сиво. Stone стои по-далеч от синьото, отколкото slate.
  noSchool: ['#78716c', '#57534e', '#a8a29e', '#d6d3d1', '#292524', '#94a3b8'],
  // Вноска по такса — административна дата, не час. Червеното е заварено от преди
  // палитрата; тук минава през същия избор като всичко друго, защото при ЧЕРВЕН бранд
  // (какъвто е secondary-ят на училището, #f91010) кръгът и цветът лъжеха едновременно.
  fee: ['#ef4444', '#7c2d12', '#0f172a', '#4c1d95', '#f97316', '#a8a29e'],
};

/**
 * 🔴 Редът е НОСЕЩ и е избран ЕМПИРИЧНО, не по правило.
 *
 * Ранният коментар тук твърдеше „който има най-малко изход, избира пръв". Звучи разумно
 * и е НЕВЯРНО за този код. Редът е измерен: „вноска по такса" избира ВТОРА, а не
 * последна, защото последна тя намира всичко заето — 405 падания в разгъвката от 2160
 * бранда срещу 225, когато избира рано. Разликата е двойна и не се вижда от четене.
 *
 * Затова редът се мени само с измерване (`npx vitest run calendarPalette` брои
 * паданията), не защото изглежда непоследователен.
 *
 * Последният кандидат на всяка роля е с КРАЙНА светлота (много тъмен или много светъл):
 * когато брандът заеме тона, единственото, което остава да разделя, е светлотата.
 */
const PICK_SEQUENCE: Record<Exclude<CalendarRole, 'lesson'>, number> = {
  lastLesson: 0,
  fee: 1,
  assistant: 2,
  vacation: 3,
  holiday: 4,
  noSchool: 5,
};

/**
 * `Record` е нарочен вместо масив: той е ИЗЧЕРПАТЕЛЕН по тип. Масивът не беше — роля,
 * забравена в него, минаваше компилацията и после `palette[role]` беше `undefined`,
 * тоест `backgroundColor: undefined` и ПРОЗРАЧНА точка. Същата тиха повреда, срещу
 * която стои `normalizeHex`. Сега пропусната роля е грешка при компилация.
 */
const PICK_ORDER = (Object.keys(PICK_SEQUENCE) as Exclude<CalendarRole, 'lesson'>[]).sort(
  (a, b) => PICK_SEQUENCE[a] - PICK_SEQUENCE[b],
);

/** Цвят на „моите часове", когато брандът липсва или е невалиден. */
const DEFAULT_PRIMARY = '#1e40af';

// ── Цветна математика ────────────────────────────────────────────────────────

const HEX = /^#?([0-9a-f]{6})$/i;

/** "#aabbcc" → [r, g, b] в 0..255. Невалиден вход дава null, не изключение. */
export function hexToRgb(hex: string): [number, number, number] | null {
  const m = HEX.exec((hex || '').trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/**
 * Канонична форма `#rrggbb` или null.
 *
 * 🔴 Задължителна е, защото палитрата връща цвят, който отива право в `backgroundColor`.
 * Брандът се пише в свободно текстово поле (`ColorPicker`) и стига до Firestore както е
 * въведен: `1e40af` (без диез) и ` #dc2626 ` (с интервали) минават проверката за валидност,
 * но CSS ги изхвърля мълчаливо и точката „мои часове" остава ПРОЗРАЧНА — а ΔE
 * математиката междувременно обявява палитрата за здрава. Гард, който мери цвят, който
 * няма да бъде нарисуван.
 */
export function normalizeHex(hex: string): string | null {
  const m = HEX.exec((hex || '').trim());
  return m ? `#${m[1].toLowerCase()}` : null;
}

function rgbToHex(r: number, g: number, b: number): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${c(r)}${c(g)}${c(b)}`;
}

/** sRGB канал 0..255 → линеен 0..1. */
function linearize(v: number): number {
  const s = v / 255;
  return s <= 0.04045 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
}

function delinearize(v: number): number {
  const s = v <= 0.0031308 ? v * 12.92 : 1.055 * Math.pow(Math.max(v, 0), 1 / 2.4) - 0.055;
  return s * 255;
}

/** CIE Lab при D65. */
export function hexToLab(hex: string): [number, number, number] | null {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const [r, g, b] = rgb.map(linearize);

  const x = (r * 0.4124564 + g * 0.3575761 + b * 0.1804375) / 0.95047;
  const y = r * 0.2126729 + g * 0.7151522 + b * 0.072175;
  const z = (r * 0.0193339 + g * 0.119192 + b * 0.9503041) / 1.08883;

  const f = (t: number) => (t > 0.008856 ? Math.cbrt(t) : 7.787 * t + 16 / 116);
  const [fx, fy, fz] = [f(x), f(y), f(z)];

  return [116 * fy - 16, 500 * (fx - fy), 200 * (fy - fz)];
}

/**
 * ΔE76 между два цвята. Невалиден вход дава 0 — тоест „неразличими", което кара
 * проверките да се провалят затворено вместо да пропуснат непроверена двойка.
 */
export function deltaE(a: string, b: string): number {
  const la = hexToLab(a);
  const lb = hexToLab(b);
  if (!la || !lb) return 0;
  return Math.sqrt((la[0] - lb[0]) ** 2 + (la[1] - lb[1]) ** 2 + (la[2] - lb[2]) ** 2);
}

/**
 * Симулация на deuteranopia по Viénot–Brettel–Mollon (1999): sRGB → линеен → LMS,
 * M каналът се възстановява от L и S, обратно към sRGB.
 *
 * Смисълът не е точна репродукция на възприятието, а измерим втори поглед: две точки,
 * които се сближават СЛЕД смачкването на червено-зелената ос, не стават за палитра.
 */
export function simulateDeuteranopia(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const [r, g, b] = rgb.map(linearize);

  const l = 17.8824 * r + 43.5161 * g + 4.11935 * b;
  const s = 0.0299566 * r + 0.184309 * g + 1.46709 * b;
  const m2 = 0.494207 * l + 1.24827 * s;

  return rgbToHex(
    delinearize(0.080944 * l - 0.130504 * m2 + 0.116721 * s),
    delinearize(-0.0102485 * l + 0.0540194 * m2 - 0.113615 * s),
    delinearize(-0.000365294 * l - 0.00412163 * m2 + 0.693513 * s),
  );
}

/**
 * Разстоянието, което се брои: ПО-МАЛКОТО от двете зрения. Цвят, който работи само за
 * едните очи, не работи.
 */
export function perceptualDistance(a: string, b: string): number {
  return Math.min(deltaE(a, b), deltaE(simulateDeuteranopia(a), simulateDeuteranopia(b)));
}

/** Прагът за двойка роли — по-строг вътре в семейството, по-мек между семействата. */
export function thresholdFor(a: CalendarRole, b: CalendarRole): number {
  return FAMILY[a] === FAMILY[b] ? MIN_DELTA_E_SAME : MIN_DELTA_E_CROSS;
}

/** Различими ли са две роли в дадена палитра. */
export function rolesDistinguishable(p: CalendarPalette, a: CalendarRole, b: CalendarRole): boolean {
  return perceptualDistance(p[a], p[b]) >= thresholdFor(a, b);
}

// ── Палитрата ────────────────────────────────────────────────────────────────

/**
 * Крайната палитра.
 *
 * Брандовият `primary` влиза както е. Всяка друга роля взима първия свой кандидат, който
 * държи изисквания праг спрямо ВСИЧКИ вече избрани. Изчерпи ли се списъкът, взима се
 * кандидатът с най-добър запас — две близки точки са по-малкото зло от произволен избор,
 * а тестът за палитрата пази това да не се случи с реалните бранд цветове.
 */
export function calendarPalette(primary?: string | null): CalendarPalette {
  const lesson = (primary ? normalizeHex(primary) : null) ?? DEFAULT_PRIMARY;

  const assigned: { role: CalendarRole; color: string }[] = [{ role: 'lesson', color: lesson }];
  const chosen = { lesson } as CalendarPalette;

  for (const role of PICK_ORDER) {
    let best = CANDIDATES[role][0];
    let bestMargin = -Infinity;

    for (const candidate of CANDIDATES[role]) {
      // Запасът се мери СПРЯМО ПРАГА, не абсолютно: 30 при праг 25 е по-добре от 34 при 35.
      const margin = Math.min(
        ...assigned.map((a) => perceptualDistance(candidate, a.color) - thresholdFor(role, a.role)),
      );
      if (margin >= 0) { best = candidate; bestMargin = margin; break; }
      if (margin > bestMargin) { best = candidate; bestMargin = margin; }
    }

    chosen[role] = best;
    assigned.push({ role, color: best });
  }

  return chosen;
}

/** Цветът на неучебен период по неговия тип; непознат тип пада на „ваканция". */
export function nonWorkingColor(palette: CalendarPalette, kind: string): string {
  if (kind === 'holiday') return palette.holiday;
  if (kind === 'no_school') return palette.noSchool;
  return palette.vacation;
}

/** Ролята на неучебен период по неговия тип — оттам идват и цветът, и формата. */
export function nonWorkingRole(kind: string): CalendarRole {
  if (kind === 'holiday') return 'holiday';
  if (kind === 'no_school') return 'noSchool';
  return 'vacation';
}

/**
 * i18n ключът за типа неучебен период — етикетът носи типа, не само името, за да остане
 * четим и когато цветът не се различава (печат, тежка цветна слепота).
 */
export function nonWorkingKindKey(kind: string): string {
  return `calendar.kind.${nonWorkingRole(kind)}`;
}

/** i18n ключът за реда на ролята в легендата. Общ за учителското и админското табло. */
export function legendKey(role: CalendarRole): string {
  return `calendar.legend.${role}`;
}

// ── Маркери за ДЕТЕ (задача 19) ──────────────────────────────────────────────

/**
 * Кандидати за цвят на едно ДЕТЕ в родителския календар.
 *
 * ⚠️ Подредени по предпочитание и избирани по СЪЩИЯ алгоритъм като ролите:
 * първият, който държи прага спрямо всичко вече заето, печели; изчерпят ли се,
 * взима се кандидатът с най-добър запас (по-малкото зло от произволен избор).
 *
 * 🔴 Списъкът е нарочно КЪС. Часовете на всички деца са едно семейство (кръгове),
 * тоест всяка двойка иска `MIN_DELTA_E_SAME`. Повече кандидати не помагат —
 * пространството, в което те се събират, е ограничено от вече заетите роли.
 */
/**
 * Ролите, които РОДИТЕЛСКИЯТ календар изобщо рисува.
 *
 * 🔴 `assistant` липсва НАРОЧНО: помощничеството е понятие на учителя
 * (`isAssistant` е винаги `false` за дете), тоест този цвят никога не се появява
 * на екрана на родителя. Изискване за разстояние спрямо цвят, който го няма,
 * стеснява пространството без да пази нищо — а при зеления production бранд
 * точно то правеше две деца „неразличими" на хартия.
 *
 * ⚠️ Появи ли се някога помощник и при родителя, ролята се връща тук — и тогава
 * границата пада, което тестът ще покаже като число.
 */
const PARENT_VISIBLE_ROLES: readonly CalendarRole[] = [
  'lesson', 'lastLesson', 'vacation', 'holiday', 'noSchool', 'fee',
];

const CHILD_CANDIDATES: readonly string[] = [
  '#15803d', // тъмнозелено — най-далеч от синия и виолетовия бранд
  '#9333ea', // виолетово
  '#065f46', // смарагдово-тъмно
  '#7c2d12', // тъмнокафяво
  '#365314', // маслинено
  '#0f766e', // тюркоазено-тъмно
  '#1e3a8a', // тъмносиньо — изход при ЧЕРВЕН бранд
  '#4a044e', // слива
];

/**
 * Цветовете на маркерите за N деца, различими помежду си И от ролите.
 *
 * 🔴 Първото дете взима цвета на ЧАСА, а не нов: при едно дете (72% от
 * семействата, измерено 16.09.2026) екранът остава какъвто е бил, а понятието
 * „час" не се нуждае от различаване. Разлика се въвежда само когато има от какво
 * да се различава.
 *
 * ⚠️ Границата е измерена, не предположена: виж теста за палитрата. Над нея
 * функцията не се чупи — тя връща най-добрия наличен кандидат, а тестът е
 * мястото, където се вижда, че запасът е паднал под прага.
 */
export function childMarkColors(primary: string | null | undefined, count: number): string[] {
  if (count <= 0) return [];
  const palette = calendarPalette(primary);
  const chosen: string[] = [palette.lesson];
  if (count === 1) return chosen;

  const roleEntries = PARENT_VISIBLE_ROLES.map((role) => ({ role, color: palette[role] }));

  for (let i = 1; i < count; i++) {
    let best = CHILD_CANDIDATES[0];
    let bestMargin = -Infinity;

    for (const candidate of CHILD_CANDIDATES) {
      if (chosen.includes(candidate)) continue;
      const margin = Math.min(
        // Спрямо ролите: маркерът на детето Е час, тоест семейство A.
        ...roleEntries.map((r) => perceptualDistance(candidate, r.color) - thresholdFor('lesson', r.role)),
        // Спрямо вече избраните деца: същото семейство, строгият праг.
        ...chosen.map((c) => perceptualDistance(candidate, c) - MIN_DELTA_E_SAME),
      );
      if (margin >= 0) { best = candidate; bestMargin = margin; break; }
      if (margin > bestMargin) { best = candidate; bestMargin = margin; }
    }

    chosen.push(best);
  }

  return chosen;
}

/**
 * Най-малкият запас спрямо прага при N деца — отрицателен значи „сливат се".
 *
 * Изнесена, за да може границата да се ИЗМЕРИ в тест и да се впише в доклада,
 * вместо да се твърди „работи до N".
 */
export function childColorMargin(primary: string | null | undefined, count: number): number {
  const colors = childMarkColors(primary, count);
  const palette = calendarPalette(primary);
  const roleEntries = PARENT_VISIBLE_ROLES.map((role) => ({ role, color: palette[role] }));
  let worst = Infinity;
  for (let i = 0; i < colors.length; i++) {
    for (const r of roleEntries) {
      if (colors[i] === palette.lesson && r.role === 'lesson') continue;
      worst = Math.min(worst, perceptualDistance(colors[i], r.color) - thresholdFor('lesson', r.role));
    }
    for (let j = i + 1; j < colors.length; j++) {
      worst = Math.min(worst, perceptualDistance(colors[i], colors[j]) - MIN_DELTA_E_SAME);
    }
  }
  return worst;
}
